<script setup lang="ts">
import { computed } from 'vue';
import { useToastStore } from '@/stores/toast.store';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  X,
} from 'lucide-vue-next';

const toast = useToastStore();

const items = computed(() => toast.items);

function iconFor(kind: 'success' | 'error' | 'warning' | 'info') {
  return kind === 'success'
    ? CheckCircle2
    : kind === 'error'
      ? XCircle
      : kind === 'warning'
        ? AlertTriangle
        : Info;
}
</script>

<template>
  <div
    class="pointer-events-none fixed inset-x-0 top-4 z-[60] flex flex-col items-center gap-2 sm:top-6 sm:right-6 sm:left-auto sm:items-end"
  >
    <transition-group
      tag="div"
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="-translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0 -translate-y-1"
      class="flex flex-col items-center gap-2 sm:items-end"
    >
      <div
        v-for="item in items"
        :key="item.id"
        class="pointer-events-auto w-[min(92vw,420px)] rounded-xl bg-white p-3.5 shadow-elevated ring-1 ring-slate-200/70"
      >
        <div class="flex items-start gap-3">
          <component
            :is="iconFor(item.kind)"
            class="mt-0.5 h-5 w-5 shrink-0"
            :class="{
              'text-emerald-500': item.kind === 'success',
              'text-amber-500': item.kind === 'warning',
              'text-red-500': item.kind === 'error',
              'text-blue-500': item.kind === 'info',
            }"
          />
          <div class="flex-1 min-w-0">
            <p
              v-if="item.title"
              class="text-sm font-semibold text-slate-900 leading-snug"
            >
              {{ item.title }}
            </p>
            <p class="text-sm text-slate-700 leading-snug break-words">
              {{ item.message }}
            </p>
          </div>
          <button
            type="button"
            class="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            :aria-label="'dismiss'"
            @click="toast.dismiss(item.id)"
          >
            <X class="h-4 w-4" />
          </button>
        </div>
      </div>
    </transition-group>
  </div>
</template>
